<template>
  <div
    v-if="isModalOpen || $isPageEditing"
    ref="modal"
    :class="['image-gallery-popup', $isPageEditing && 'image-gallery-popup--editor']"
    @keydown.esc="closeModal"
  >
    <div class="container image-gallery-popup__container">
      <div
        class="image-gallery-popup__modal"
        :aria-label="(fields.title?.value || 'Image gallery') + ' - dialog'"
        role="dialog"
        aria-modal="true"
      >
        <div class="image-gallery-popup__header">
          <sc-text
            :field="fields.title"
            tag="h2"
            class="image-gallery-popup__heading"
            :class="titleClassess"
          />
          <button class="image-gallery-popup__close" :aria-label="t('close')" @click="closeModal">
            <i class="icon icon-Close" aria-hidden="true"></i>
          </button>
        </div>
        <div class="image-gallery-popup__content">
          <sc-placeholder name="image-gallery-popup-placeholder" :rendering="rendering" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef, watch, computed } from 'vue';
import { Placeholder as ScPlaceholder, Text as ScText } from '@sitecore-jss/sitecore-jss-vue';
import { useI18n } from 'vue-i18n';
import { useRegisterDataLayerAction } from '../../data-layer/helpers/registerDataLayer';
import { usePopupModal } from '../../composables/usePopupModal';
import { useJssStore } from '@/stores/jssStore';
import type { ImageGalleryPopupProps } from './types';

const { t } = useI18n();

const jssStore = useJssStore();

defineEmits<{ click: [] }>();

const props = withDefaults(defineProps<ImageGalleryPopupProps>(), {
  fields: () => ({}),
  params: () => ({}),
});

const modal = useTemplateRef('modal');

const registerDataLayerAction = useRegisterDataLayerAction();

const titleClassess = computed(() => [jssStore.brandName === 'FlamingGrill' ? 'font-l' : 'font-m']);

const handleRegisterDataLayerGallery = (event: string) => {
  registerDataLayerAction({
    event_name: event,
    component: 'gallery_base',
  });
};

const onModalClose = () => {
  handleRegisterDataLayerGallery('close_gallery');
};

const onModalOpen = () => {
  handleRegisterDataLayerGallery('open_gallery');
};

const { closeModal, isModalOpen } = usePopupModal({
  popupID: props.fields.popupID?.value,
  modalRef: modal,
});

watch(isModalOpen, (newValue) => {
  newValue ? onModalOpen() : onModalClose();
});
</script>

<style lang="scss">
@import './scss/ImageGalleryPopup.scss';
</style>
